import React, { useCallback, useEffect, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Countdown, IconChevronRight } from '@noths/polaris-client-ribbons-design-system';
import { getTimeNHoursBefore } from '@noths/polaris-client-utils';

import type { TopBannerRootCSS } from './TopBanner.styles';
import { trackBannerClick, trackBannerImpression } from './tracking';
import * as styles from './TopBanner.styles';

export const MESSAGE_CHANGE_INTERVAL_MS = 7000;

export interface TopBannerMessage {
  backgroundColor: string;
  countdown?: {
    endDate: string;
    message: string;
    threshold: number;
  };
  linkUrl?: string;
  message: string;
  promoCode?: string;
  textColor: 'Light' | 'Dark';
}

export interface TopBannerProps {
  messages: TopBannerMessage[];
  sticky?: boolean;
}

export const TopBanner = ({ messages, sticky }: TopBannerProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(messages.length > 1);
  const tokenRegex = /({promocode}|{countdown})/gi;

  const refEl = useImpressionRef<HTMLDivElement>(() => {
    trackBannerImpression(messages[0].message);
  });

  const changeToPreviousMessage = useCallback(
    (disableAutoAdvance: boolean = false) => {
      if (disableAutoAdvance) {
        setAutoAdvance(false);
      }
      setCurrentMessageIndex((prevIndex) =>
        prevIndex === 0 ? messages.length - 1 : prevIndex - 1,
      );
    },
    [messages.length],
  );

  const changeToNextMessage = useCallback(
    (disableAutoAdvance: boolean = false) => {
      if (disableAutoAdvance) {
        setAutoAdvance(false);
      }
      setCurrentMessageIndex((prevIndex) =>
        prevIndex === messages.length - 1 ? 0 : prevIndex + 1,
      );
    },
    [messages.length],
  );

  const handleMouseOver = () => {
    setAutoAdvance(false);
  };

  const handleMouseOut = () => {
    setAutoAdvance(messages.length > 1);
  };

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    if (autoAdvance) {
      intervalId = setInterval(() => {
        changeToNextMessage(false);
      }, MESSAGE_CHANGE_INTERVAL_MS);
    } else if (intervalId) {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [autoAdvance, changeToNextMessage]);

  const currentMessage = messages[currentMessageIndex];
  const enableButtons = messages.length > 1;
  const iconColor =
    currentMessage.textColor === 'Light'
      ? 'var(--neutral-white-base)'
      : 'var(--neutral-black-base)';

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <section
      aria-label="Top banner messages"
      css={[styles.root, styles.textColor[currentMessage.textColor]]}
      onMouseOut={handleMouseOut}
      onMouseOver={handleMouseOver}
      style={
        {
          '--topBannerPosition': sticky ? 'sticky' : 'relative',
          backgroundColor: currentMessage.backgroundColor,
        } as TopBannerRootCSS
      }
    >
      <button
        aria-label="Previous message"
        css={styles.arrowButton}
        disabled={!enableButtons}
        onClick={() => changeToPreviousMessage(true)}
        type="button"
      >
        <IconChevronRight color={iconColor} height={16} width={16} />
      </button>
      {messages.map(({ countdown, linkUrl, message, promoCode }, index) => {
        const key = linkUrl ? `${message}-${linkUrl}` : message;
        const showCountdown =
          countdown && Date.now() > getTimeNHoursBefore(countdown.endDate, countdown.threshold);
        const parts = showCountdown
          ? countdown.message.split(tokenRegex)
          : message.split(tokenRegex);

        const renderedMessage = parts.map((part, partIndex) => {
          const key = `part-${partIndex}`;

          if (part.toLowerCase() === '{promocode}' && promoCode) {
            return promoCode;
          }

          if (part.toLowerCase() === '{countdown}' && countdown) {
            return <Countdown endDateTime={countdown.endDate} key={key} />;
          }

          return part;
        });

        return (
          <div
            aria-hidden={index !== currentMessageIndex}
            aria-live="polite"
            css={[styles.message]}
            key={key}
            ref={index === 0 ? refEl : undefined}
          >
            {linkUrl ? (
              <a
                href={linkUrl}
                onClick={(ev) => trackBannerClick(ev, message, linkUrl)}
                tabIndex={index === currentMessageIndex ? 0 : -1}
              >
                {renderedMessage}
              </a>
            ) : (
              renderedMessage
            )}
          </div>
        );
      })}
      <button
        aria-label="Next message"
        css={styles.arrowButton}
        disabled={!enableButtons}
        onClick={() => changeToNextMessage(true)}
        type="button"
      >
        <IconChevronRight color={iconColor} height={16} width={16} />
      </button>
    </section>
  );
};
